
import { useCallback, useState } from "react";
import { useSceneStore } from "../stores/sceneStore";
import { api } from "../api";
import { autoUpdateThumbnail } from "../lib/autoThumbnail";

/** Save status for feedback UI */
export type SaveStatus = "idle" | "saving" | "saved" | "error";

/**
 * Hook for persisting space edits to the database.
 * Requires edit permission (admin for curated, owner for user spaces).
 */
export function useSaveSpace() {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  const activeSpaceDescriptor = useSceneStore((s) => s.activeSpaceDescriptor);
  const isDirty = useSceneStore((s) => s.isDirty);
  const setDirty = useSceneStore((s) => s.setDirty);
  const activeSpaceId = useSceneStore((s) => s.activeSpaceId);

  const handleSave = useCallback(async () => {
    if (!activeSpaceDescriptor || !activeSpaceId) return;

    setSaveStatus("saving");
    setSaveError(null);

    try {
      // Save the semantic SpaceDescriptor — server compiles rendering on demand
      const updatedSpace = {
        ...activeSpaceDescriptor,
        updatedAt: new Date().toISOString(),
      };

      const saved = await api.saveSpace(updatedSpace);

      // Update the stored descriptor with the saved version
      useSceneStore.setState({ activeSpaceDescriptor: saved, error: null });
      setDirty(false);
      setSaveStatus("saved");
      setSaveError(null);
      void autoUpdateThumbnail(activeSpaceId);

      // Auto-reset status after 2 seconds
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to save space";
      setSaveError(message);
      useSceneStore.getState().setError(message);
      setSaveStatus("idle");
    }
  }, [activeSpaceDescriptor, activeSpaceId, setDirty]);

  return {
    saveStatus,
    saveError,
    isDirty,
    canSave: !!activeSpaceDescriptor && isDirty,
    handleSave,
  };
}
